import { api } from './api';

export type NameDisplayFormat = 'first_last' | 'last_first';

export interface CompanySettings {
  id: string;
  name: string;
  website: string | null;
  timezone: string;
  logoUrl: string | null;
  seasonalEffectsEnabled: boolean;
  employeeNameDisplayFormat: NameDisplayFormat;
  // Identity
  legalName: string | null;
  industry: string | null;
  companySize: string | null;
  foundedYear: number | null;
  // Contact
  email: string | null;
  phone: string | null;
  supportEmail: string | null;
  // Registered address
  addressStreet: string | null;
  addressStreet2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
  // Legal & tax
  registrationNumber: string | null;
  taxId: string | null;
  // Localization
  dateFormat: string | null;
  currency: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanySettingsResponse {
  success: boolean;
  data: CompanySettings;
}

export interface UpdateCompanySettingsInput {
  name: string;
  website?: string;
  timezone: string;
  seasonalEffectsEnabled?: boolean;
  employeeNameDisplayFormat?: NameDisplayFormat;
  // Identity
  legalName?: string;
  industry?: string;
  companySize?: string;
  foundedYear?: number | null;
  // Contact
  email?: string;
  phone?: string;
  supportEmail?: string;
  // Registered address
  addressStreet?: string;
  addressStreet2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  // Legal & tax
  registrationNumber?: string;
  taxId?: string;
  // Localization
  dateFormat?: string;
  currency?: string;
}

export interface UploadLogoResponse {
  success: boolean;
  data: {
    logoUrl: string;
  };
  message: string;
}

export interface AppearanceSettings {
  employeeNameDisplayFormat: NameDisplayFormat;
}

/**
 * Appearance settings — readable by any authenticated user (not admin-gated).
 */
export async function getAppearanceSettings(): Promise<AppearanceSettings> {
  const { data } = await api.get<{ success: boolean; data: AppearanceSettings }>(
    '/settings/appearance'
  );
  return data.data;
}

/**
 * Get company settings
 */
export async function getCompanySettings(): Promise<CompanySettings> {
  const { data } = await api.get<CompanySettingsResponse>('/settings/company');
  return data.data;
}

/**
 * Update company settings
 */
export async function updateCompanySettings(
  input: UpdateCompanySettingsInput
): Promise<CompanySettings> {
  const { data } = await api.put<CompanySettingsResponse>('/settings/company', input);
  return data.data;
}

/**
 * Upload company logo
 */
export async function uploadCompanyLogo(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('logo', file);

  const { data } = await api.post<UploadLogoResponse>('/settings/company/logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data.data.logoUrl;
}
